import { useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function CompleteGuideKidneyTransplantAbroad() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const costComparison = [
    {
      country: 'Pakistan',
      costRange: 'USD 120,000–150,000',
      waitingTime: 'Variable, hospital-dependent',
      evaluation: 'Required'
    },
    {
      country: 'India',
      costRange: 'USD 100,000–140,000',
      waitingTime: 'Variable, hospital-dependent',
      evaluation: 'Required'
    },
    {
      country: 'Cambodia',
      costRange: 'USD 110,000–145,000',
      waitingTime: 'Variable, hospital-dependent',
      evaluation: 'Required'
    },
    {
      country: 'Europe (General)',
      costRange: 'USD 150,000–250,000',
      waitingTime: 'Variable, hospital-dependent',
      evaluation: 'Required'
    }
  ];

  const faqItems = [
    {
      question: 'What is kidney transplant abroad?',
      answer: 'Kidney transplant abroad refers to the option of receiving kidney transplant surgery at a licensed medical facility outside your home country. Patients explore international kidney transplant options for various reasons including extended waiting times domestically, cost considerations, or access to specialized transplant centers. This involves coordination with licensed hospitals, qualified transplant surgeons, and compliance with all applicable medical and legal regulations in the destination country.'
    },
    {
      question: 'How much does kidney transplant cost abroad?',
      answer: 'The estimated kidney transplant cost abroad varies significantly by country and hospital. General estimates range from USD 100,000 to USD 250,000 depending on the destination. For example, kidney transplant in Pakistan typically ranges from USD 120,000–150,000, while India may range from USD 100,000–140,000. These are estimated figures only. Final pricing is determined by the treating hospital based on individual medical circumstances, required tests, medications, and length of stay. All cost estimates should be confirmed directly with the medical facility.'
    },
    {
      question: 'Why does kidney transplant cost vary between countries?',
      answer: 'Kidney transplant cost abroad varies due to multiple factors including local healthcare infrastructure costs, surgeon expertise and experience, hospital facility standards, required pre-transplant testing, post-operative care protocols, medication costs, and length of hospital stay. Additionally, currency exchange rates, local regulatory requirements, and the complexity of individual medical cases affect final pricing. Cost differences do not necessarily reflect quality of care—many international transplant centers maintain high medical standards at lower operational costs than facilities in higher-cost regions.'
    },
    {
      question: 'How long is the waiting time for kidney transplant abroad?',
      answer: 'Waiting time for kidney transplant abroad varies significantly and depends on multiple factors including medical eligibility, donor availability, hospital capacity, and individual health circumstances. Unlike domestic deceased-donor waiting lists that may extend 3-7 years in some countries, international programs with living donor arrangements may have shorter coordination timelines. However, no specific waiting time can be guaranteed. Each case requires individual medical evaluation by the treating hospital, and timelines are determined by licensed physicians based on medical suitability and regulatory compliance.'
    },
    {
      question: 'Is this service medical treatment?',
      answer: 'No. We provide informational and coordination support only. We are not a medical provider, hospital, or treatment facility. We do not diagnose conditions, prescribe treatments, or make medical decisions. All medical care, including kidney transplant surgery, is provided exclusively by licensed physicians at accredited hospitals. All medical decisions—including eligibility determination, treatment planning, and surgical procedures—remain entirely with qualified medical professionals. Our role is limited to providing information, facilitating communication with hospitals, and assisting with logistical coordination.'
    },
    {
      question: 'Are kidney transplant outcomes guaranteed?',
      answer: 'No. Kidney transplant outcomes cannot be guaranteed. Like all major surgical procedures, kidney transplantation carries inherent medical risks including but not limited to organ rejection, infection, surgical complications, and medication side effects. Success rates vary based on individual health factors, donor compatibility, surgical expertise, and post-operative care adherence. While modern transplant medicine has achieved significant advances with general 1-year survival rates of 85-95% globally, individual outcomes depend on numerous medical variables. All outcome discussions must occur directly with your treating physician who can assess your specific medical circumstances.'
    },
    {
      question: 'How are kidney donors regulated in international transplant programs?',
      answer: 'All kidney transplant programs we coordinate with operate under strict ethical and legal frameworks that prohibit organ trafficking and illegal donor arrangements. Donors must be medically evaluated, provide informed consent, and meet all regulatory requirements of the destination country. Most international programs work exclusively with living related donors (family members) or altruistic donors who meet stringent medical and psychological screening criteria. All donor arrangements must comply with the Declaration of Istanbul principles, local transplant laws, and international medical ethics standards. We do not arrange donors—donor identification and evaluation are managed entirely by licensed medical facilities.'
    },
    {
      question: 'What factors affect kidney transplant eligibility abroad?',
      answer: 'Kidney transplant eligibility is determined by licensed physicians based on comprehensive medical evaluation. Factors affecting eligibility include current kidney function levels, overall health status, presence of active infections or cancer, cardiovascular health, body mass index, psychological readiness, ability to comply with post-transplant medication regimens, and donor compatibility. Age alone is not necessarily disqualifying—medical suitability is assessed individually. Patients with certain conditions such as active substance abuse, severe heart disease, or active malignancy may not be suitable candidates. Eligibility determination requires submission of complete medical records to the treating hospital for physician review.'
    },
    {
      question: 'What documents are required to explore kidney transplant abroad?',
      answer: 'Initial inquiry typically requires recent medical records including kidney function tests (creatinine, GFR), dialysis records if applicable, blood type, viral screening results (HIV, Hepatitis B/C, CMV), cardiovascular evaluation, and any relevant imaging studies. Complete documentation requirements are provided by the treating hospital during the evaluation process. Medical records should be recent (typically within 3-6 months) and translated into English if originally in another language. Additional testing may be required by the hospital before final eligibility determination. Passport validity and visa requirements for the destination country should also be confirmed early in the process.'
    },
    {
      question: 'How do I start exploring kidney transplant abroad options?',
      answer: 'Begin by consulting with your current nephrologist to discuss whether international kidney transplant may be medically appropriate for your situation. If your physician supports further exploration, gather your recent medical records and contact us for informational support. We can provide general cost estimates, explain the coordination process, and facilitate communication with licensed transplant hospitals for medical evaluation. The hospital will review your records and determine medical eligibility. Throughout this process, all medical decisions remain with licensed physicians. We provide coordination support only—not medical advice or treatment.'
    }
  ];

  const references = [
    {
      category: 'International Organizations',
      sources: [
        { name: 'Global Observatory on Donation and Transplantation (GODT)', url: 'https://www.transplant-observatory.org/' },
        { name: 'International Registry in Organ Donation and Transplantation (IRODaT)', url: 'https://www.irodat.org/' },
        { name: 'World Health Organization - Transplantation', url: 'https://www.who.int/health-topics/transplantation' }
      ]
    },
    {
      category: 'National Transplant Registries',
      sources: [
        { name: 'OPTN - Organ Procurement and Transplantation Network (United States)', url: 'https://optn.transplant.hrsa.gov/' },
        { name: 'NHS Blood and Transplant (United Kingdom)', url: 'https://www.nhsbt.nhs.uk/' },
        { name: 'ANZDATA Registry (Australia/New Zealand)', url: 'https://www.anzdata.org.au/' }
      ]
    },
    {
      category: 'Medical Literature',
      sources: [
        { name: 'PubMed Central (PMC)', url: 'https://www.ncbi.nlm.nih.gov/pmc/' },
        { name: 'The Lancet', url: 'https://www.thelancet.com/' },
        { name: 'Transplantation Journal', url: 'https://journals.lww.com/transplantjournal/' }
      ]
    },
    {
      category: 'Professional Societies',
      sources: [
        { name: 'The Transplantation Society (TTS)', url: 'https://www.tts.org/' },
        { name: 'American Society of Nephrology (ASN)', url: 'https://www.asn-online.org/' },
        { name: 'European Society for Organ Transplantation (ESOT)', url: 'https://www.esot.org/' }
      ]
    }
  ];

  // Structured Data
  useEffect(() => {
    const structuredData = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Article',
          headline: 'Complete Guide to Kidney Transplant Abroad (2026)',
          description: 'Comprehensive guide covering kidney transplant abroad options, estimated costs by region, waiting times, process overview, ethical framework, and frequently asked questions for international kidney transplant coordination.',
          author: {
            '@type': 'Organization',
            name: 'Kidney Transplant Abroad'
          },
          publisher: {
            '@type': 'Organization',
            name: 'Kidney Transplant Abroad'
          },
          datePublished: '2026-01-01',
          dateModified: new Date().toISOString().split('T')[0]
        },
        {
          '@type': 'FAQPage',
          mainEntity: faqItems.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: {
              '@type': 'Answer',
              text: faq.answer
            }
          }))
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: 'Home',
              item: 'https://kidneypatientsupport.com/'
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Complete Guide to Kidney Transplant Abroad',
              item: 'https://kidneypatientsupport.com/complete-guide-kidney-transplant-abroad'
            }
          ]
        }
      ]
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* SEO Meta Tags */}
      <title>Complete Guide to Kidney Transplant Abroad (2026) | Cost, Process & Eligibility</title>
      <meta name="description" content="Comprehensive guide to kidney transplant abroad covering estimated costs by region, waiting times, process steps, ethical framework, and eligibility requirements for international kidney transplant coordination." />
      <meta name="keywords" content="kidney transplant abroad, international kidney transplant, kidney transplant cost abroad, kidney transplant overseas, kidney transplant process, kidney transplant eligibility" />
      <link rel="canonical" href="https://kidneypatientsupport.com/complete-guide-kidney-transplant-abroad" />
      <meta name="last-modified" content={new Date().toISOString().split('T')[0]} />
      
      {/* Open Graph */}
      <meta property="og:title" content="Complete Guide to Kidney Transplant Abroad (2026)" />
      <meta property="og:description" content="Comprehensive guide covering kidney transplant abroad options, costs, process, and eligibility requirements." />
      <meta property="og:type" content="article" />
      <meta property="og:url" content="https://kidneypatientsupport.com/complete-guide-kidney-transplant-abroad" />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Complete Guide to Kidney Transplant Abroad (2026)" />
      <meta name="twitter:description" content="Comprehensive guide to international kidney transplant coordination, costs, and process." />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-teal-50 via-white to-blue-50 py-24">
        <div className="max-w-5xl mx-auto px-6">
          <h1 className="text-5xl font-bold text-gray-900 mb-6 leading-tight">
            Complete Guide to Kidney Transplant Abroad
          </h1>
          <p className="text-xl text-gray-700 leading-relaxed mb-8">
            A comprehensive resource for patients exploring <strong>kidney transplant abroad</strong> options. This guide provides detailed information on estimated costs by region, waiting time considerations, the coordination process, ethical frameworks, and eligibility requirements for <strong>international kidney transplant</strong> programs worldwide.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link 
              to="/contact" 
              className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition whitespace-nowrap"
            >
              Request Information
            </Link>
            <Link 
              to="/pricing" 
              className="inline-block bg-white text-teal-600 px-8 py-4 rounded-lg font-semibold border-2 border-teal-600 hover:bg-teal-50 transition whitespace-nowrap"
            >
              View Cost Estimates
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              For patients facing extended domestic waiting lists, rising treatment costs, or limited access to transplant centers, <strong>kidney transplant abroad</strong> has become an increasingly explored pathway. This comprehensive guide covers everything you need to know about pursuing a kidney transplant at a licensed medical facility outside your home country—including estimated costs, coordination timelines, eligibility requirements, and the ethical framework that governs <strong>international kidney transplant</strong> programs.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Patients explore kidney transplant abroad for several reasons. In some countries, deceased-donor waiting lists can extend 3-7 years or longer, during which time patients remain dependent on dialysis with associated health risks and quality of life limitations. Additionally, the total <Link to="/kidney-transplant-cost-abroad" className="text-teal-600 hover:text-teal-700 font-semibold">kidney transplant cost abroad</Link> in certain regions may be significantly lower than in higher-cost healthcare systems, while maintaining equivalent medical standards and expertise at accredited facilities.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Our role is to provide <strong>informational and coordination support</strong> for patients considering international kidney transplant options. We are not a medical provider, hospital, or treatment facility. We do not diagnose conditions, prescribe treatments, or make medical decisions. Our services include providing general cost estimates, explaining the <Link to="/process" className="text-teal-600 hover:text-teal-700 font-semibold">coordination process</Link>, facilitating communication with licensed transplant hospitals, and assisting with logistical arrangements such as travel and accommodation.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              All medical care—including eligibility determination, pre-transplant evaluation, surgical procedures, and post-operative management—is provided exclusively by licensed physicians at accredited hospitals. All medical decisions remain entirely with qualified medical professionals. Patients should consult with their current nephrologist before exploring international options to ensure this pathway is medically appropriate for their individual circumstances. For a full overview of our <Link to="/ethics" className="text-teal-600 hover:text-teal-700 font-semibold">ethical and legal commitments</Link>, please review our dedicated ethics page.
            </p>
          </div>
        </div>
      </section>

      {/* Average Cost Section - NEW H2 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Average Cost of Kidney Transplant Worldwide
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Understanding the <strong>average cost of kidney transplant worldwide</strong> is essential for patients exploring international options. Kidney transplant costs vary dramatically by country, reflecting differences in healthcare infrastructure, labor costs, regulatory environments, and local economic factors. This section provides a comprehensive overview of estimated costs across major regions and explains the factors that contribute to price variations.
          </p>

          <div className="overflow-x-auto mb-8">
            <table className="w-full border-collapse bg-white shadow-lg rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-teal-600 text-white">
                  <th className="px-6 py-4 text-left font-semibold">Country/Region</th>
                  <th className="px-6 py-4 text-left font-semibold">Estimated Cost Range (USD)</th>
                  <th className="px-6 py-4 text-left font-semibold">General Waiting Time Range</th>
                  <th className="px-6 py-4 text-left font-semibold">Hospital Evaluation Required</th>
                </tr>
              </thead>
              <tbody>
                {costComparison.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-4 font-semibold text-gray-900">{item.country}</td>
                    <td className="px-6 py-4 text-gray-700">{item.costRange}</td>
                    <td className="px-6 py-4 text-gray-700">{item.waitingTime}</td>
                    <td className="px-6 py-4 text-gray-700">{item.evaluation}</td>
                  </tr>
                ))}
                <tr className="bg-white">
                  <td className="px-6 py-4 font-semibold text-gray-900">United States</td>
                  <td className="px-6 py-4 text-gray-700">USD 400,000–500,000+</td>
                  <td className="px-6 py-4 text-gray-700">3-5 years (deceased donor)</td>
                  <td className="px-6 py-4 text-gray-700">Required</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="px-6 py-4 font-semibold text-gray-900">United Kingdom</td>
                  <td className="px-6 py-4 text-gray-700">Covered by NHS (public)</td>
                  <td className="px-6 py-4 text-gray-700">2-3 years (deceased donor)</td>
                  <td className="px-6 py-4 text-gray-700">Required</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Cost Components Breakdown
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                The total <strong>kidney transplant cost abroad</strong> typically includes multiple components that patients should understand when evaluating options:
              </p>
              <ul className="space-y-3 text-gray-700 ml-6">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Pre-transplant evaluation:</strong> Comprehensive medical testing including blood work, imaging studies, cardiovascular assessment, infectious disease screening, and compatibility testing (typically USD 5,000-15,000)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Surgical procedure:</strong> Operating room costs, surgical team fees, anesthesia, and medical supplies (typically USD 40,000-80,000)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Hospitalization:</strong> Post-operative care, intensive care if needed, nursing care, and facility fees (typically 2-4 weeks, USD 20,000-50,000)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Immunosuppressive medications:</strong> Initial supply of anti-rejection medications (typically 3-6 months, USD 5,000-15,000)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Donor evaluation and care:</strong> Medical evaluation, surgical procedure, and post-operative care for living donors (typically USD 15,000-30,000)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Follow-up care:</strong> Post-discharge monitoring, laboratory tests, and physician consultations before returning home (typically USD 5,000-10,000)</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Additional Costs to Consider
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Beyond the medical costs quoted by hospitals, patients should budget for additional expenses associated with <strong>international kidney transplant</strong> coordination:
              </p>
              <ul className="space-y-3 text-gray-700 ml-6">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>International travel:</strong> Round-trip airfare for patient and companion (typically USD 1,000-3,000 per person)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Accommodation:</strong> Extended stay near hospital for 4-8 weeks (typically USD 2,000-6,000 total)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Visa and documentation:</strong> Medical visa fees, document translation, and notarization (typically USD 200-500)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Local transportation:</strong> Airport transfers, hospital visits, and daily transportation (typically USD 500-1,500)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Ongoing medications:</strong> Lifelong immunosuppressive medications after returning home (typically USD 10,000-20,000 annually)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Follow-up care at home:</strong> Regular monitoring by local nephrologist, laboratory tests, and clinic visits (costs vary by insurance coverage)</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Why Costs Vary Between Countries
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                The significant variation in <strong>kidney transplant cost abroad</strong> reflects multiple economic and healthcare system factors:
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Healthcare infrastructure costs:</strong> Countries with lower operational costs for hospitals, medical equipment, and facility maintenance can offer lower prices while maintaining quality standards. Modern transplant centers in Pakistan, India, and Cambodia operate with equivalent medical technology and expertise but benefit from lower real estate, utilities, and administrative costs compared to facilities in the United States or Western Europe.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Labor and professional fees:</strong> Physician salaries, nursing staff wages, and support personnel costs vary significantly by country. Highly qualified transplant surgeons in South Asia may charge USD 10,000-20,000 for a kidney transplant procedure, while comparable surgeons in the United States may charge USD 50,000-100,000 or more. This reflects local economic conditions rather than differences in training or expertise—many international transplant surgeons have trained at leading institutions worldwide.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Regulatory and insurance environments:</strong> Healthcare systems with extensive regulatory requirements, malpractice insurance costs, and administrative overhead tend to have higher prices. The United States healthcare system, with its complex insurance billing, regulatory compliance requirements, and high malpractice insurance costs, contributes to significantly higher transplant costs compared to countries with streamlined regulatory frameworks.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>Currency exchange rates:</strong> Favorable exchange rates can make kidney transplant abroad more affordable for patients from countries with stronger currencies. However, patients should factor in potential currency fluctuations when planning budgets and confirm final pricing in their home currency with the treating hospital.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-8">
            <p className="text-gray-800 leading-relaxed">
              <strong>Important:</strong> All cost estimates are subject to change and must be confirmed directly with the treating hospital. Final pricing is determined by licensed medical facilities based on comprehensive medical evaluation, required pre-transplant testing, surgical complexity, post-operative care needs, and medication protocols. Patients should request detailed cost breakdowns in writing before committing to treatment.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Link to="/kidney-transplant-cost-abroad" className="block p-6 bg-teal-50 rounded-lg hover:bg-teal-100 transition">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Detailed Cost Breakdown</h3>
              <p className="text-gray-700 mb-3">Understand what's included in kidney transplant cost estimates and factors affecting final pricing.</p>
              <span className="text-teal-600 font-semibold">View Cost Guide →</span>
            </Link>
            <Link to="/pakistan" className="block p-6 bg-teal-50 rounded-lg hover:bg-teal-100 transition">
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Kidney Transplant in Pakistan</h3>
              <p className="text-gray-700 mb-3">Estimated cost: USD 120,000–150,000. Learn about Pakistan's transplant program and coordination process.</p>
              <span className="text-teal-600 font-semibold">Learn More →</span>
            </Link>
          </div>
        </div>
      </section>

      {/* Waiting Time Section - NEW H2 */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            Waiting Time for Kidney Transplant in Different Countries
          </h2>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            <strong>Waiting time for kidney transplant</strong> varies dramatically across countries and depends on whether patients are pursuing deceased-donor transplants through national waiting lists or living-donor transplants through international coordination. Understanding these differences is crucial for patients evaluating their options. For a detailed breakdown, visit our guide on <Link to="/transplant-waiting-time-abroad" className="text-teal-600 hover:text-teal-700 font-semibold">transplant waiting time abroad</Link>.
          </p>

          <div className="space-y-6 mb-8">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Deceased-Donor Waiting Times by Country
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                For patients on deceased-donor waiting lists, median waiting times vary significantly by country based on organ donation rates, population size, and allocation systems:
              </p>
              <div className="bg-gray-50 p-6 rounded-lg mb-4">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 font-bold">•</span>
                    <span><strong>United States:</strong> Median waiting time of 3-5 years, with significant regional variation. Some regions report waiting times exceeding 7 years for certain blood types. According to OPTN data, over 90,000 patients are currently on the kidney transplant waiting list.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 font-bold">•</span>
                    <span><strong>United Kingdom:</strong> Median waiting time of 2-3 years through NHS Blood and Transplant. Waiting times vary by blood type, with O blood type patients typically waiting longer than other blood types.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 font-bold">•</span>
                    <span><strong>Canada:</strong> Median waiting time of 3-5 years, with provincial variation. Some provinces report waiting times of 4-6 years depending on blood type and medical urgency.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 font-bold">•</span>
                    <span><strong>Australia:</strong> Median waiting time of 2-4 years according to ANZDATA Registry. Waiting times have gradually increased due to growing demand and limited deceased-donor availability.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-3 font-bold">•</span>
                    <span><strong>Spain:</strong> Median waiting time of 1-2 years. Spain maintains one of the world's highest organ donation rates, resulting in shorter waiting times compared to most other countries.</span>
                  </li>
                </ul>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                These waiting times represent median values—individual waiting times vary based on blood type, tissue compatibility, medical urgency, geographic location, and time on the waiting list. Patients with rare blood types or high levels of antibodies may experience significantly longer waiting times.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Living-Donor Transplant Timelines
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                For patients with identified living donors (family members or altruistic donors), coordination timelines differ significantly from deceased-donor waiting lists:
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Domestic living-donor programs:</strong> In countries with established living-donor transplant programs, coordination typically takes 3-6 months from initial evaluation to surgery. This timeline includes comprehensive medical evaluation of both recipient and donor, compatibility testing, psychological assessment, regulatory approvals, and surgical scheduling.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>International living-donor programs:</strong> For <strong>kidney transplant abroad</strong> with living donors, coordination timelines vary based on medical eligibility determination, completion of required testing, donor evaluation and approval, hospital capacity, visa processing, and travel arrangements. Some patients may complete the process within several months, while others may require longer timelines based on medical complexity or regulatory requirements.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                No specific waiting time can be guaranteed for international programs. Each case requires individual medical evaluation by the treating hospital, and timelines are determined by licensed physicians based on medical suitability and regulatory compliance.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Factors Affecting Waiting Time Abroad
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Multiple factors influence coordination timelines for <strong>international kidney transplant</strong>:
              </p>
              <ul className="space-y-3 text-gray-700 ml-6">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Medical eligibility review:</strong> Comprehensive evaluation by hospital physicians to determine suitability for transplant surgery (typically 1-2 weeks)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Pre-transplant testing completion:</strong> Required blood work, imaging studies, cardiovascular assessment, and infectious disease screening (typically 2-4 weeks)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Donor evaluation and approval:</strong> Medical and psychological evaluation of living donors, compatibility testing, and regulatory approvals (typically 4-8 weeks)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Hospital scheduling:</strong> Availability of surgical teams, operating rooms, and post-operative care facilities (varies by hospital capacity)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Visa processing:</strong> Medical visa application and approval for destination country (typically 1-4 weeks depending on country)</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Travel arrangements:</strong> Flight booking, accommodation coordination, and logistical preparation (typically 1-2 weeks)</span>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Managing Expectations About Waiting Time
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                Patients should maintain realistic expectations about waiting times for <strong>kidney transplant abroad</strong>. While international programs may offer shorter coordination timelines compared to multi-year deceased-donor waiting lists, no specific timeline can be promised or guaranteed. Medical safety, thorough evaluation, and regulatory compliance take priority over speed. Patients should be prepared for potential delays due to medical findings, additional testing requirements, or unforeseen circumstances. Clear communication with the treating hospital throughout the process helps manage expectations and ensures appropriate planning.
              </p>
            </div>
          </div>

          <Link to="/transplant-waiting-time-abroad" className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition whitespace-nowrap">
            Learn More About Waiting Times
          </Link>
        </div>
      </section>

      {/* Countries Section - NEW H2 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            Countries Performing Kidney Transplants for International Patients
          </h2>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Several countries worldwide have established <strong>kidney transplant programs</strong> that accept international patients. These programs operate at licensed hospitals with experienced transplant teams, modern facilities, and compliance with local regulatory frameworks. Understanding the options available helps patients make informed decisions about where to pursue <strong>kidney transplant abroad</strong>.
          </p>

          <div className="space-y-8 mb-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Pakistan
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Pakistan has emerged as a significant destination for <strong>international kidney transplant</strong> coordination. The country maintains several accredited transplant centers in major cities including Karachi, Lahore, and Islamabad. These facilities are staffed by qualified transplant surgeons, many of whom have trained internationally, and equipped with modern surgical technology and post-operative care capabilities.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Estimated cost:</strong> USD 120,000–150,000 for complete kidney transplant including pre-operative evaluation, surgery, hospitalization, initial medications, and follow-up care before returning home.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Regulatory framework:</strong> Pakistan's transplant program operates under the Transplantation of Human Organs and Tissues Ordinance, which regulates donor eligibility, recipient evaluation, and ethical standards. All transplants must be approved by hospital ethics committees and comply with national regulations.
              </p>
              <Link to="/pakistan" className="text-teal-600 hover:text-teal-700 font-semibold">
                Learn more about kidney transplant in Pakistan →
              </Link>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                India
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                India has a well-established medical tourism industry and numerous accredited transplant centers across major cities including Delhi, Mumbai, Chennai, and Bangalore. Indian transplant hospitals often maintain international accreditations such as JCI (Joint Commission International) and employ transplant surgeons with extensive experience and international training.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Estimated cost:</strong> USD 100,000–140,000 for complete kidney transplant including all medical services, though costs vary by hospital and city.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Regulatory framework:</strong> India's transplant program operates under the Transplantation of Human Organs Act, which strictly regulates living-donor transplants and prohibits organ commercialization. Only related donors or altruistic donors approved by authorization committees are permitted.
              </p>
              <Link to="/india" className="text-teal-600 hover:text-teal-700 font-semibold">
                Learn more about kidney transplant in India →
              </Link>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Cambodia
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Cambodia has developed transplant capabilities in recent years with licensed facilities in Phnom Penh. These centers serve both domestic and international patients and maintain modern surgical equipment and trained transplant teams.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Estimated cost:</strong> USD 110,000–145,000 for complete kidney transplant services.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                <strong>Regulatory framework:</strong> Cambodia's transplant program operates under national health regulations governing organ transplantation, donor eligibility, and medical facility licensing.
              </p>
              <Link to="/cambodia" className="text-teal-600 hover:text-teal-700 font-semibold">
                Learn more about kidney transplant in Cambodia →
              </Link>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Other International Destinations
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Additional countries with established transplant programs accepting international patients include:
              </p>
              <ul className="space-y-3 text-gray-700 ml-6 mb-4">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Turkey:</strong> Modern transplant centers in Istanbul and Ankara with international accreditations. Estimated costs: USD 60,000-90,000.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Mexico:</strong> Transplant facilities in major cities serving North American patients. Estimated costs: USD 80,000-120,000.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Thailand:</strong> Well-established medical tourism infrastructure with accredited transplant centers. Estimated costs: USD 70,000-100,000.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Philippines:</strong> Transplant programs in Manila and other major cities. Estimated costs: USD 60,000-90,000.</span>
                </li>
              </ul>
              <p className="text-lg text-gray-700 leading-relaxed">
                Each country maintains its own regulatory framework, medical standards, and cost structures. Patients should research specific hospitals, verify licensing and accreditation, and confirm all details directly with treating facilities before making decisions.
              </p>
            </div>
          </div>

          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg mb-8">
            <p className="text-gray-800 leading-relaxed">
              <strong>Important:</strong> The availability of international kidney transplant programs does not guarantee eligibility or outcomes. All patients must undergo comprehensive medical evaluation by licensed physicians at the treating hospital to determine suitability. Regulatory requirements, donor eligibility criteria, and medical standards vary by country and must be carefully reviewed before proceeding.
            </p>
          </div>

          <Link to="/countries" className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition whitespace-nowrap">
            Compare Countries & Programs
          </Link>
        </div>
      </section>

      {/* Risks and Ethics Section - NEW H2 */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            Risks and Ethical Considerations
          </h2>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Understanding both the medical risks and ethical considerations associated with <strong>kidney transplant abroad</strong> is essential for informed decision-making. All major surgical procedures carry inherent risks, and international coordination introduces additional considerations that patients must carefully evaluate. Our full <Link to="/ethics" className="text-teal-600 hover:text-teal-700 font-semibold">ethical guidelines and compliance standards</Link> are available for detailed review.
          </p>

          <div className="space-y-8 mb-8">
            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Medical Risks of Kidney Transplant Surgery
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Like all major surgical procedures, kidney transplantation carries inherent medical risks that patients must understand and discuss thoroughly with their treating physician:
              </p>
              <div className="bg-red-50 p-6 rounded-lg mb-4">
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2 font-bold">•</span>
                    <span><strong>Organ rejection:</strong> The recipient's immune system may recognize the transplanted kidney as foreign tissue and attempt to reject it. Acute rejection occurs in approximately 10-20% of transplant recipients within the first year despite immunosuppressive medications. Chronic rejection can occur over years and may eventually lead to graft failure.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2 font-bold">•</span>
                    <span><strong>Infection risks:</strong> Immunosuppressive medications required to prevent rejection also suppress the body's ability to fight infections. Transplant recipients face increased risk of bacterial, viral, and fungal infections, particularly in the first 3-6 months post-transplant.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2 font-bold">•</span>
                    <span><strong>Surgical complications:</strong> Potential complications include bleeding, blood clots, wound infections, urinary tract complications, and vascular complications affecting blood flow to the transplanted kidney.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2 font-bold">•</span>
                    <span><strong>Medication side effects:</strong> Immunosuppressive medications can cause side effects including increased cancer risk, diabetes, high blood pressure, bone disease, and kidney toxicity over time.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2 font-bold">•</span>
                    <span><strong>Cardiovascular complications:</strong> Transplant recipients face increased risk of heart disease, stroke, and other cardiovascular events.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2 font-bold">•</span>
                    <span><strong>Graft failure:</strong> The transplanted kidney may fail to function properly immediately after surgery (primary non-function) or may fail over time due to rejection, medication non-compliance, or other factors.</span>
                  </li>
                </ul>
              </div>
              <p className="text-lg text-gray-700 leading-relaxed">
                These risks apply to all kidney transplants regardless of location. Patients must discuss their individual risk profile with their treating physician based on comprehensive medical evaluation. Risk levels vary based on recipient age, overall health status, comorbidities, donor compatibility, and adherence to post-transplant care protocols.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Additional Considerations for International Transplant
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                Pursuing <strong>kidney transplant abroad</strong> introduces additional considerations beyond standard transplant risks:
              </p>
              <ul className="space-y-3 text-gray-700 ml-6 mb-4">
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Distance from home support systems:</strong> Patients will be away from family, friends, and familiar healthcare providers during the critical post-operative period.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Language and cultural barriers:</strong> Communication challenges may arise despite translation services, potentially affecting understanding of medical instructions.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Travel during recovery:</strong> International travel shortly after major surgery carries additional risks and requires physician clearance.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Continuity of care:</strong> Ensuring seamless transition from international transplant center to local nephrologist for ongoing follow-up care requires careful coordination.</span>
                </li>
                <li className="flex items-start">
                  <span className="text-teal-600 mr-2 font-bold">•</span>
                  <span><strong>Emergency complications abroad:</strong> If serious complications arise during the stay abroad, patients must rely on the local healthcare system rather than their familiar home providers.</span>
                </li>
              </ul>
              <p className="text-lg text-gray-700 leading-relaxed">
                These considerations do not necessarily preclude international transplant but should be carefully evaluated as part of the decision-making process. Patients should discuss these factors with their nephrologist and family members before proceeding.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Ethical Framework and Compliance
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed mb-4">
                All <strong>international kidney transplant</strong> coordination must operate within strict ethical and legal frameworks designed to protect patients, donors, and medical professionals:
              </p>
              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-red-600 mb-4">
                <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-close-circle-line text-red-600 text-2xl mr-2"></i>
                  Strictly Prohibited Practices
                </h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2 font-bold">✗</span>
                    <span><strong>Organ trafficking:</strong> The buying and selling of human organs is illegal under international law and prohibited by the Declaration of Istanbul. We maintain zero tolerance for any form of organ commercialization.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2 font-bold">✗</span>
                    <span><strong>Coerced donation:</strong> All donors must provide voluntary informed consent without coercion, financial inducement, or undue pressure from any party.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2 font-bold">✗</span>
                    <span><strong>Exploitation of vulnerable populations:</strong> Practices that exploit economically disadvantaged individuals, prisoners, or other vulnerable populations are strictly prohibited.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2 font-bold">✗</span>
                    <span><strong>Circumventing regulations:</strong> All transplant procedures must comply fully with local laws and regulations. Attempts to circumvent legal requirements are prohibited.</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-teal-600 mb-4">
                <h4 className="text-xl font-semibold text-gray-900 mb-3 flex items-center">
                  <i className="ri-checkbox-circle-line text-teal-600 text-2xl mr-2"></i>
                  Required Ethical Standards
                </h4>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2 font-bold">✓</span>
                    <span><strong>Licensed facilities only:</strong> All transplant procedures must be performed at properly licensed hospitals with appropriate regulatory approvals and oversight.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2 font-bold">✓</span>
                    <span><strong>Qualified medical professionals:</strong> All surgeons must be qualified transplant specialists with appropriate training, certification, and experience.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2 font-bold">✓</span>
                    <span><strong>Informed consent:</strong> Both recipients and donors must provide informed consent after receiving comprehensive information about risks, benefits, and alternatives.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2 font-bold">✓</span>
                    <span><strong>Donor protection:</strong> All donors must undergo thorough medical and psychological evaluation to ensure suitability and voluntary participation.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2 font-bold">✓</span>
                    <span><strong>Transparency:</strong> All costs, procedures, and potential outcomes must be clearly communicated to patients in advance.</span>
                  </li>
                </ul>
              </div>

              <p className="text-lg text-gray-700 leading-relaxed">
                We are committed to upholding the highest ethical standards in international kidney transplant coordination. Any practices that violate medical ethics, exploit vulnerable individuals, or circumvent legal regulations are strictly prohibited. Patients should report any concerns about ethical compliance to appropriate regulatory authorities.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                Patient Responsibility and Due Diligence
              </h3>
              <p className="text-lg text-gray-700 leading-relaxed">
                Patients considering <strong>kidney transplant abroad</strong> have a responsibility to conduct appropriate due diligence. This includes verifying hospital licensing and accreditation, confirming surgeon qualifications and experience, understanding all costs and payment terms in writing, reviewing the ethical framework governing the program, ensuring compliance with local regulations, and consulting with their home nephrologist throughout the process. Patients should never proceed with any arrangement that appears to violate ethical standards or legal requirements, regardless of cost savings or promised timelines.
              </p>
            </div>
          </div>

          <Link to="/ethics" className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition whitespace-nowrap">
            Read Full Ethical Guidelines
          </Link>
        </div>
      </section>

      {/* How Coordination Works Section - NEW H2 */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-gray-900 mb-8">
            How International Transplant Coordination Works
          </h2>
          
          <p className="text-lg text-gray-700 leading-relaxed mb-8">
            Understanding how <strong>international transplant coordination</strong> works helps patients navigate the process with realistic expectations and appropriate preparation. Our role is to provide informational and logistical support while all medical decisions remain with licensed physicians at accredited hospitals. For a complete step-by-step walkthrough, visit our dedicated <Link to="/process" className="text-teal-600 hover:text-teal-700 font-semibold">kidney transplant process guide</Link>.
          </p>

          <div className="space-y-6 mb-8">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                1
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Initial Information Request & Consultation</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Patients begin by submitting an information request providing basic medical background, current health status, and preliminary questions. We provide general cost estimates for different countries, explain the coordination process timeline, answer initial questions about logistics and requirements, and recommend consultation with the patient's current nephrologist before proceeding.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>What we provide:</strong> General information, cost estimates, process overview, and answers to logistical questions. <strong>What we don't provide:</strong> Medical advice, eligibility determination, or treatment recommendations—these remain with licensed physicians.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                2
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Medical Records Gathering & Submission</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Patients gather recent medical records from their treating physicians. Required documentation typically includes kidney function tests (creatinine, GFR, BUN), dialysis records if applicable, blood type and antibody screening, viral screening results (HIV, Hepatitis B/C, CMV, EBV), cardiovascular evaluation (ECG, echocardiogram), chest X-ray or CT scan, complete blood count and metabolic panel, and any relevant imaging studies of kidneys or urinary system.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We facilitate submission of these records to licensed transplant hospitals for physician review. Medical records should be recent (typically within 3-6 months) and translated into English if originally in another language. We can assist with document organization and translation coordination if needed.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                3
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Hospital Medical Evaluation & Eligibility Determination</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  The hospital's transplant team reviews submitted medical records and determines medical eligibility. This evaluation is performed exclusively by licensed physicians based on comprehensive assessment of kidney function, overall health status, cardiovascular health, presence of active infections or cancer, ability to tolerate major surgery and immunosuppressive medications, and donor compatibility if a living donor is identified.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  <strong>Important:</strong> Not all patients will be deemed suitable candidates. Medical eligibility is determined entirely by hospital physicians based on established medical criteria. We facilitate communication but do not influence medical decisions. If deemed ineligible, the hospital will explain the medical reasons and may suggest alternative options or additional testing that could change eligibility status.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                4
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Direct Physician Communication & Treatment Planning</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  If deemed medically eligible, patients communicate directly with the treating hospital's transplant team. The hospital provides a detailed treatment plan including required pre-operative testing, surgical procedure details, expected hospitalization duration, post-operative care protocols, and medication regimens. The hospital confirms final pricing in writing with detailed cost breakdown and explains payment terms and schedule.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Patients have the opportunity to ask medical questions directly to the transplant surgeon and medical team. We facilitate these communications through translation services if needed but do not interpret or modify medical information. All medical decisions—including whether to proceed with treatment—remain entirely with the patient and their physicians.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                5
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Travel & Logistics Coordination</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  Once the patient decides to proceed and the hospital confirms scheduling, we assist with logistical arrangements including medical visa application support and documentation, international flight booking assistance for patient and companion, accommodation near the hospital for extended stay (typically 4-8 weeks), local transportation coordination for hospital visits, and communication support throughout the stay.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Patients should plan for extended stays to allow for pre-operative preparation, surgery, post-operative monitoring, and initial recovery before receiving physician clearance for international travel. Rushing the recovery process to return home early is medically inadvisable and increases complication risks.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                6
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Treatment at Licensed Hospital</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  All medical care is provided exclusively by licensed physicians at accredited hospitals. This includes final pre-operative testing and preparation, kidney transplant surgery performed by qualified transplant surgeons, post-operative monitoring in hospital (typically 2-4 weeks), medication management and adjustment, complication management if needed, and discharge planning with detailed instructions.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  We provide communication support and assist with non-medical needs during the hospital stay, but all medical care and decisions remain entirely with the hospital's medical team. Patients should follow all medical instructions carefully and report any concerns immediately to their treating physicians.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                7
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Post-Discharge Recovery & Return Home Preparation</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  After hospital discharge, patients typically remain near the hospital for additional monitoring before receiving physician clearance for international travel. This period allows for continued recovery, regular follow-up appointments with the transplant team, laboratory testing to monitor kidney function and medication levels, medication adjustment as needed, and assessment of readiness for travel.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Before returning home, the hospital provides comprehensive discharge documentation including complete medical records and surgical reports, detailed medication instructions and prescriptions, laboratory results and monitoring schedules, emergency contact information, and recommendations for follow-up care with local nephrologist. For detailed guidance on preparing for the return home, review our <Link to="/after-return-preparation" className="text-teal-600 hover:text-teal-700 font-semibold">after-return preparation guide</Link>.
                </p>
              </div>
            </div>

            <div className="flex items-start">
              <div className="flex-shrink-0 w-12 h-12 bg-teal-600 text-white rounded-full flex items-center justify-center font-bold text-xl mr-4">
                8
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Ongoing Follow-Up Care at Home</h3>
                <p className="text-gray-700 leading-relaxed mb-3">
                  After returning home, patients require ongoing follow-up care with their local nephrologist to monitor kidney function through regular laboratory testing, manage immunosuppressive medications and adjust dosages as needed, monitor for signs of rejection or complications, manage medication side effects, and provide long-term transplant care.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Patients should establish care with their local nephrologist before traveling abroad and ensure their physician is willing to provide post-transplant follow-up care. The international transplant center can provide guidance to the local physician regarding medication protocols and monitoring schedules. Lifelong medical follow-up is essential for transplant success—patients must commit to regular monitoring and medication adherence.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-teal-50 p-6 rounded-lg mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">Our Role in Coordination</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">What We Do:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span>Provide general cost estimates and process information</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span>Facilitate communication with licensed hospitals</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span>Assist with travel and accommodation logistics</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span>Coordinate document submission and translation</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-teal-600 mr-2">✓</span>
                    <span>Provide communication support during stay abroad</span>
                  </li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">What We Don't Do:</h4>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✗</span>
                    <span>Provide medical treatment or diagnosis</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✗</span>
                    <span>Make medical decisions or determine eligibility</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✗</span>
                    <span>Arrange organ donors</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✗</span>
                    <span>Guarantee medical outcomes or waiting times</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-red-600 mr-2">✗</span>
                    <span>Provide post-transplant medical care</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <Link to="/process" className="inline-block bg-teal-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-teal-700 transition whitespace-nowrap">
            View Detailed Process Guide
          </Link>
        </div>
      </section>

      {/* Final Reinforcement Paragraph */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <p className="text-lg text-gray-700 leading-relaxed">
            This guide is designed to help patients navigate <strong>international kidney transplant options</strong> with clarity and confidence. Whether you are comparing the <strong>estimated kidney transplant cost</strong> across different regions, evaluating waiting time considerations, or understanding the ethical standards that govern cross-border transplant coordination, our goal is to provide accurate, transparent information. All medical procedures referenced in this guide are performed exclusively at <strong>licensed hospitals</strong> by qualified transplant surgeons, and all medical decisions remain with treating physicians. We encourage patients to use this resource alongside professional medical advice from their nephrologist to make informed decisions about their kidney transplant journey.
          </p>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-gradient-to-br from-teal-600 to-teal-700">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Explore Your Options?
          </h2>
          <p className="text-xl text-teal-50 mb-8 leading-relaxed">
            Request information about kidney transplant abroad coordination. We provide informational support only—all medical decisions remain with licensed physicians.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-white text-teal-600 px-10 py-5 rounded-lg font-bold text-lg hover:bg-teal-50 transition whitespace-nowrap shadow-lg"
          >
            Request Information
          </Link>
          <p className="text-teal-100 mt-6 text-sm">
            No medical guarantees. Outcomes depend on individual medical circumstances and physician-determined treatment plans.
          </p>
        </div>
      </section>

      {/* Comprehensive Disclaimer */}
      <section className="py-12 bg-amber-50 border-t-4 border-amber-500">
        <div className="max-w-4xl mx-auto px-6">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">Important Medical & Legal Disclaimer</h3>
          <div className="text-sm text-gray-700 leading-relaxed space-y-3">
            <p>
              <strong>Not Medical Advice:</strong> This guide provides general information only and does not constitute medical advice, diagnosis, or treatment recommendations. All medical decisions must be made in consultation with licensed physicians based on comprehensive evaluation of your individual medical circumstances.
            </p>
            <p>
              <strong>No Guarantees:</strong> Kidney transplant outcomes and waiting times cannot be guaranteed. Success rates and timelines vary based on individual health factors, donor compatibility, surgical expertise, and post-operative care adherence.
            </p>
            <p>
              <strong>Cost Estimates:</strong> All cost figures are estimates only and subject to change. Final pricing is determined by treating hospitals based on individual medical circumstances, required testing, medications, and length of stay.
            </p>
            <p>
              <strong>Physician Authority:</strong> All medical decisions—including eligibility determination, treatment planning, and surgical procedures—are made exclusively by licensed physicians at accredited hospitals. We provide coordination support only.
            </p>
            <p>
              <strong>Consult Your Doctor:</strong> Before exploring kidney transplant abroad, consult with your current nephrologist to ensure this option is medically appropriate for your situation.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
